import { useEffect, useRef } from "react"

export function useKeyboardShortcuts({
  selectedItems,
  categories,
  breadcrumb,
  showSearch,
  showAddNode,
  showMoveNode,
  showRename,
  modalJustClosed,
  setBreadcrumb,
  setSelectedItems,
  setShowRename,
  setShowAddNode,
  setShowSearch,
  handleOpenProperties,
  children,
}) {
  const lastNavIndexRef = useRef(0)

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return
      if (showSearch || showAddNode || showMoveNode || showRename) return
      if (modalJustClosed) return

      switch (e.key) {
        case "Enter":
          if (selectedItems.length === 1) {
            const cat = categories.find((c) => c.id === selectedItems[0])
            if (cat) {
              setBreadcrumb((prev) => [...prev, { id: cat.id, name: cat.name }])
              setSelectedItems([])
            }
          }
          break

        case "Backspace":
          if (breadcrumb.length > 1) {
            e.preventDefault()
            const currentId = breadcrumb[breadcrumb.length - 1].id
            setBreadcrumb((prev) => prev.slice(0, -1))
            setSelectedItems([currentId])
          }
          break

        case "F2":
          if (selectedItems.length === 1) {
            e.preventDefault()
            setShowRename(true)
          }
          break

        case " ":
          if (selectedItems.length === 1 && !selectedItems.includes(1)) {
            e.preventDefault()
            handleOpenProperties?.()
          }
          break

        case "Escape":
          setSelectedItems([])
          break

        case "a":
          e.preventDefault()
          setShowAddNode(true)
          break

        case "s":
          e.preventDefault()
          setShowSearch(true)
          break

        case "ArrowRight":
        case "ArrowLeft":
        case "ArrowDown":
        case "ArrowUp":
          e.preventDefault()
          handleArrowNav(e.key, e.shiftKey)
          break

        default:
          break
      }
    }

    const handleArrowNav = (key, shift) => {
      if (children.length === 0) return
      const childIds = children.map((c) => c.id)

      if (selectedItems.length === 0) {
        const idx = key === "ArrowLeft" || key === "ArrowUp" ? childIds.length - 1 : 0
        lastNavIndexRef.current = idx
        setSelectedItems([childIds[idx]])
        return
      }

      const lastId = selectedItems[selectedItems.length - 1]
      let curr = childIds.indexOf(lastId)
      if (curr === -1) curr = lastNavIndexRef.current

      const grid = document.querySelector(".taxonomy-grid")
      const cols = Math.max(1, Math.floor((grid?.clientWidth || 0) / (280 + 16)))

      let next = curr
      if (key === "ArrowRight") next = Math.min(curr + 1, childIds.length - 1)
      else if (key === "ArrowLeft") next = Math.max(curr - 1, 0)
      else if (key === "ArrowDown") next = Math.min(curr + cols, childIds.length - 1)
      else if (key === "ArrowUp") next = Math.max(curr - cols, 0)

      if (next === curr) return
      lastNavIndexRef.current = next
      const targetId = childIds[next]

      if (shift) {
        const anchorIdx = childIds.indexOf(selectedItems[0])
        if (anchorIdx !== -1) {
          const s = Math.min(anchorIdx, next)
          const end = Math.max(anchorIdx, next)
          setSelectedItems(childIds.slice(s, end + 1))
        } else {
          setSelectedItems([targetId])
        }
      } else {
        setSelectedItems([targetId])
      }

      setTimeout(() => {
        const cards = document.querySelectorAll(".taxonomy-card")
        cards[next]?.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }, 0)
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [
    selectedItems, breadcrumb, showSearch, showAddNode, showMoveNode, showRename,
    categories, modalJustClosed, setBreadcrumb, setSelectedItems, setShowRename,
    setShowAddNode, setShowSearch, handleOpenProperties, children,
  ])
}
